import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Search,
  Plus,
  TrendingUp,
  TrendingDown,
  Users,
  Briefcase,
  BarChart3,
  ChevronDown,
  X,
  Minus
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency as fmtCur, getCurrencySymbol } from '@/lib/market';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortfolioManagementPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [selectedAssetClass, setSelectedAssetClass] = useState('INV');
  const [selectedFOTab, setSelectedFOTab] = useState('stock_fo');
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState('all');
  const [showAddPortfolio, setShowAddPortfolio] = useState(false);
  const [searchInstruments, setSearchInstruments] = useState('');
  
  // Transaction form state (for INV)
  const [transactionType, setTransactionType] = useState('buy');
  const [transactionDate, setTransactionDate] = useState(new Date().toISOString().split('T')[0]);
  const [fundName, setFundName] = useState('');
  const [quantity, setQuantity] = useState('0');
  const [nav, setNav] = useState('0.00');
  const [netAmount, setNetAmount] = useState('0.00');
  const [stampCharges, setStampCharges] = useState('0.00');
  const [grossAmount, setGrossAmount] = useState('0.00');

  // Add Portfolio form state (for F&O)
  const [newPortfolio, setNewPortfolio] = useState({
    name: '',
    fullName: '',
    address: '',
    city: '',
    country: '',
    pincode: '',
    pan: ''
  });

  // Mock market data
  const marketData = {
    sensex: { value: '83,294.66', change: '+479.95', percent: '+0.58%', isUp: true },
    nifty: { value: '25,713.00', change: '+141.75', percent: '+0.55%', isUp: true }
  };

  // Asset classes
  const assetClasses = [
    { id: 'INV', label: 'INV', description: 'Investments' },
    { id: 'FO', label: 'F&O', description: 'Futures & Options' }
  ];

  // F&O Sub-tabs
  const foTabs = [
    { id: 'stock_fo', label: 'Stock F&O', badge: 'S' },
    { id: 'ot', label: 'OT', badge: 'OT' },
    { id: 'others', label: 'Others', badge: '' }
  ];

  // Mock F&O instruments data
  const [foInstruments, setFoInstruments] = useState([
    { id: '1', instrument: 'NIFTY 25000 CE', pl: '+12,500', realisedPL: '+8,200', totalPL: '+20,700' },
    { id: '2', instrument: 'BANKNIFTY 52000 PE', pl: '-3,200', realisedPL: '+15,400', totalPL: '+12,200' },
    { id: '3', instrument: 'RELIANCE FUT', pl: '+5,800', realisedPL: '+2,100', totalPL: '+7,900' },
    { id: '4', instrument: 'TCS 4200 CE', pl: '-1,500', realisedPL: '+4,300', totalPL: '+2,800' },
    { id: '5', instrument: 'HDFC FUT', pl: '+8,900', realisedPL: '-1,200', totalPL: '+7,700' }
  ]);

  // Mock portfolios
  const [portfolios, setPortfolios] = useState([
    { id: '1', name: 'Primary Portfolio', family: 'family1', value: '$2.4M', change: '+8.2%' },
    { id: '2', name: 'Growth Fund', family: 'family1', value: '$1.8M', change: '+12.1%' },
    { id: '3', name: 'Income Portfolio', family: 'family2', value: '$3.2M', change: '+5.7%' }
  ]);

  const families = [
    { id: 'all', name: 'All', icon: 'grid' },
    { id: 'family1', name: 'Family Group', icon: 'users' },
  ];

  // Calculate amounts (for INV)
  useEffect(() => {
    const qty = parseFloat(quantity) || 0;
    const navVal = parseFloat(nav) || 0;
    const stamp = parseFloat(stampCharges) || 0;
    const net = qty * navVal;
    setNetAmount(net.toFixed(2));
    setGrossAmount((net + stamp).toFixed(2));
  }, [quantity, nav, stampCharges]);

  const handleSaveTransaction = () => {
    if (!selectedPortfolio) {
      toast.error('Please select a portfolio first');
      return;
    }
    if (!fundName) {
      toast.error('Please enter a fund name');
      return;
    }
    toast.success('Transaction saved successfully');
    // Reset form
    setFundName('');
    setQuantity('0');
    setNav('0.00');
    setStampCharges('0.00');
  };

  const handleAddPortfolio = (e) => {
    e.preventDefault();
    if (!newPortfolio.name) {
      toast.error('Portfolio name is required');
      return;
    }
    
    const portfolio = {
      id: Date.now().toString(),
      name: newPortfolio.name,
      family: selectedFamily === 'all' ? 'family1' : selectedFamily,
      value: '$0',
      change: '0%',
      ...newPortfolio
    };
    setPortfolios([...portfolios, portfolio]);
    setShowAddPortfolio(false);
    setSelectedPortfolio(portfolio.id);
    setNewPortfolio({
      name: '',
      fullName: '',
      address: '',
      city: '',
      country: '',
      pincode: '',
      pan: ''
    });
    toast.success(`Portfolio "${newPortfolio.name}" created`);
  };

  const filteredPortfolios = selectedFamily === 'all' 
    ? portfolios 
    : portfolios.filter(p => p.family === selectedFamily);

  const filteredInstruments = foInstruments.filter(i => 
    i.instrument.toLowerCase().includes(searchInstruments.toLowerCase())
  );

  // Calculate totals for F&O
  const currSym = getCurrencySymbol(user?.market);
  const totalCharges = `${currSym}2,450`; // Mock value
  const totalProfitLoss = filteredInstruments.reduce((acc, i) => {
    const val = parseFloat(i.totalPL.replace(/[+,]/g, '')) || 0;
    return acc + val;
  }, 0);

  return (
    <div className="p-6" data-testid="portfolio-management-page">
      {/* Asset Class Tabs */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-1">
          {assetClasses.map((ac) => (
            <button
              key={ac.id}
              onClick={() => setSelectedAssetClass(ac.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedAssetClass === ac.id
                  ? 'bg-[#ffd87a] text-white'
                  : 'bg-[#f5f5f5] text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-[#e5e5e5]'
              }`}
              data-testid={`asset-class-${ac.id.toLowerCase()}`}
            >
              {ac.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        {selectedAssetClass === 'INV' && (
          <Button
            onClick={() => setShowAddPortfolio(true)}
            className="bg-[#ffd87a] hover:bg-[#f5cc6a] text-black text-white"
            data-testid="add-portfolio-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Portfolio
          </Button>
        )}
        {selectedAssetClass === 'FO' && (
          <Button
            onClick={() => setShowAddPortfolio(true)}
            className="border border-[#e5e5e5] hover:bg-[#f5f5f5] text-[#4a4a4a] bg-transparent"
            data-testid="add-portfolio-btn"
          >
            <Plus className="w-4 h-4 mr-2 text-[#ffd87a]" />
            Add Portfolio
          </Button>
        )}
      </div>

      {/* Portfolio selector for INV */}
      {selectedAssetClass === 'INV' && (
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            {families.map((family) => (
              <button
                key={family.id}
                onClick={() => setSelectedFamily(family.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  selectedFamily === family.id
                    ? 'bg-[#e5e5e5] text-[#1a1a1a]'
                    : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'
                }`}
              >
                {family.icon === 'users' ? (
                  <Users className="w-4 h-4" />
                ) : (
                  <BarChart3 className="w-4 h-4" />
                )}
                {family.name}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {filteredPortfolios.map((portfolio) => (
              <button
                key={portfolio.id}
                onClick={() => setSelectedPortfolio(portfolio.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  selectedPortfolio === portfolio.id
                    ? 'bg-[#ffd87a] text-white font-medium'
                    : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'
                }`}
                data-testid={`portfolio-${portfolio.id}`}
              >
                <Briefcase className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{portfolio.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

        {/* INV View - Transaction Form */}
        {selectedAssetClass === 'INV' && (
          <div className="max-w-2xl mx-auto">
            <h1 className="text-xl font-semibold text-[#1a1a1a] mb-6">SIF - Buy/Sell</h1>

              <div className="space-y-5">
                {/* Transaction Type */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">Trans. Type</label>
                  <div className="col-span-2">
                    <Select value={transactionType} onValueChange={setTransactionType}>
                      <SelectTrigger className="bg-white border-[#e5e5e5] text-[#1a1a1a]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#e5e5e5]">
                        <SelectItem value="buy" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">Buy</SelectItem>
                        <SelectItem value="sell" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">Sell</SelectItem>
                        <SelectItem value="switch_in" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">Switch In</SelectItem>
                        <SelectItem value="switch_out" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">Switch Out</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Date */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">Date</label>
                  <div className="col-span-2">
                    <Input
                      type="date"
                      value={transactionDate}
                      onChange={(e) => setTransactionDate(e.target.value)}
                      className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                    />
                  </div>
                </div>

                {/* Fund Name */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">Fund Name</label>
                  <div className="col-span-2 flex gap-2">
                    <Select value={fundName} onValueChange={setFundName}>
                      <SelectTrigger className="flex-1 bg-white border-[#e5e5e5] text-[#1a1a1a]">
                        <SelectValue placeholder="Select fund..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#e5e5e5]">
                        <SelectItem value="hdfc_equity" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">HDFC Equity Fund</SelectItem>
                        <SelectItem value="icici_bluechip" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">ICICI Bluechip Fund</SelectItem>
                        <SelectItem value="sbi_small_cap" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">SBI Small Cap Fund</SelectItem>
                        <SelectItem value="axis_midcap" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">Axis Midcap Fund</SelectItem>
                        <SelectItem value="kotak_flexicap" className="text-[#1a1a1a] hover:bg-[#f5f5f5]">Kotak Flexicap Fund</SelectItem>
                      </SelectContent>
                    </Select>
                    <button className="p-2 rounded-lg bg-[#f5f5f5] border border-[#e5e5e5] text-[#4a4a4a] hover:text-[#1a1a1a] transition-colors">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Quantity */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">Quantity</label>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="bg-white border-[#e5e5e5] text-[#1a1a1a] text-right"
                      min="0"
                    />
                  </div>
                </div>

                {/* NAV */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">NAV</label>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={nav}
                      onChange={(e) => setNav(e.target.value)}
                      className="bg-white border-[#e5e5e5] text-[#1a1a1a] text-right"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                {/* Net Amount */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">Net Amount</label>
                  <div className="col-span-2">
                    <Input
                      type="text"
                      value={netAmount}
                      readOnly
                      className="bg-[#f9f9f9] border-[#e5e5e5] text-[#4a4a4a] text-right"
                    />
                  </div>
                </div>

                {/* Stamp Charges */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">Stamp Charges</label>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      value={stampCharges}
                      onChange={(e) => setStampCharges(e.target.value)}
                      className="bg-white border-[#e5e5e5] text-[#1a1a1a] text-right"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                {/* Gross Amount */}
                <div className="grid grid-cols-3 gap-4 items-center">
                  <label className="text-sm text-[#4a4a4a]">Gross Amount</label>
                  <div className="col-span-2">
                    <Input
                      type="text"
                      value={grossAmount}
                      readOnly
                      className="bg-[#f9f9f9] border-[#e5e5e5] text-[#4a4a4a] text-right"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={handleSaveTransaction}
                    className="bg-[#ffd87a] hover:bg-[#ffd87a] text-white px-6"
                    data-testid="save-transaction-btn"
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFundName('');
                      setQuantity('0');
                      setNav('0.00');
                      setStampCharges('0.00');
                    }}
                    className="border-[#e5e5e5] text-[#4a4a4a] hover:bg-[#f5f5f5]"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* F&O View - Instruments Table */}
          {selectedAssetClass === 'FO' && (
            <div className="h-full flex flex-col">
              {/* F&O Sub-tabs */}
              <div className="flex items-center gap-2 mb-4">
                {foTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedFOTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedFOTab === tab.id
                        ? 'bg-[#ffd87a] text-white'
                        : 'bg-[#f5f5f5] text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-[#e5e5e5]'
                    }`}
                    data-testid={`fo-tab-${tab.id}`}
                  >
                    {tab.badge && (
                      <span className={`w-5 h-5 rounded flex items-center justify-center text-xs ${
                        selectedFOTab === tab.id ? 'bg-white/20' : 'bg-[#e5e5e5]'
                      }`}>
                        {tab.badge}
                      </span>
                    )}
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search Instruments */}
              <div className="flex items-center gap-4 mb-4">
                <div className="relative flex-1 max-w-md">
                  <Minus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a4a4a]" />
                  <Search className="absolute left-8 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a4a4a]" />
                  <Input
                    type="text"
                    value={searchInstruments}
                    onChange={(e) => setSearchInstruments(e.target.value)}
                    placeholder="Search Instruments"
                    className="pl-14 bg-white border-[#e5e5e5] text-[#1a1a1a] text-sm placeholder:text-[#4a4a4a]"
                  />
                </div>
              </div>

              {/* Instruments Table */}
              <div className="flex-1 bg-white/80 rounded-lg border border-[#e5e5e5] overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e5e5e5]">
                      <th className="text-left px-4 py-3 text-sm font-medium text-[#4a4a4a]">Instrument</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-[#4a4a4a]">P / L</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-[#4a4a4a]">Realised P / L</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-[#4a4a4a]">Total P / L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInstruments.map((instrument) => (
                      <tr key={instrument.id} className="border-b border-[#e5e5e5]/50 hover:bg-[#f9f9f9] transition-colors">
                        <td className="px-4 py-3 text-sm text-[#1a1a1a]">{instrument.instrument}</td>
                        <td className={`px-4 py-3 text-sm text-right ${
                          instrument.pl.startsWith('+') ? 'text-emerald-600' : 'text-[#c97a3a]'
                        }`}>
                          {instrument.pl}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right ${
                          instrument.realisedPL.startsWith('+') ? 'text-emerald-600' : 'text-[#c97a3a]'
                        }`}>
                          {instrument.realisedPL}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right font-medium ${
                          instrument.totalPL.startsWith('+') ? 'text-emerald-600' : 'text-[#c97a3a]'
                        }`}>
                          {instrument.totalPL}
                        </td>
                      </tr>
                    ))}
                    {filteredInstruments.length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-[#4a4a4a]">
                          No instruments found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Bottom Summary */}
              <div className="mt-4 p-4 bg-white/80 rounded-lg border border-[#e5e5e5]">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-[#4a4a4a]">
                    Less Total Charges (GST, STT, others)
                  </div>
                  <div className="text-sm text-[#4a4a4a]">{totalCharges}</div>
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#e5e5e5]">
                  <div className="text-sm font-medium text-[#1a1a1a]">
                    Total Profit / Loss
                  </div>
                  <div className={`text-lg font-semibold ${totalProfitLoss >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]'}`}>
                    {totalProfitLoss >= 0 ? '+' : ''}{fmtCur(totalProfitLoss, user?.market)}
                  </div>
                </div>
              </div>
            </div>
          )}

      {/* Add Portfolio Modal */}
      {showAddPortfolio && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-[#1a1a1a]">
                {selectedAssetClass === 'FO' ? 'Add F&O Portfolio' : 'Add Portfolio'}
              </h2>
              <button
                onClick={() => setShowAddPortfolio(false)}
                className="p-1 rounded-lg hover:bg-[#f5f5f5] text-[#4a4a4a] hover:text-[#4a4a4a] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddPortfolio}>
              <div className="space-y-4">
                {/* Portfolio Name */}
                <div>
                  <label className="text-sm text-[#4a4a4a] mb-1 block">
                    Portfolio Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={newPortfolio.name}
                    onChange={(e) => setNewPortfolio({...newPortfolio, name: e.target.value})}
                    placeholder="Enter portfolio name"
                    className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                    required
                  />
                </div>

                {/* Full Name (F&O only) */}
                {selectedAssetClass === 'FO' && (
                  <>
                    <div>
                      <label className="text-sm text-[#4a4a4a] mb-1 block">Full Name</label>
                      <Input
                        value={newPortfolio.fullName}
                        onChange={(e) => setNewPortfolio({...newPortfolio, fullName: e.target.value})}
                        placeholder="Enter full name"
                        className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#4a4a4a] mb-1 block">Address</label>
                      <textarea
                        value={newPortfolio.address}
                        onChange={(e) => setNewPortfolio({...newPortfolio, address: e.target.value})}
                        placeholder="Enter address"
                        className="w-full px-3 py-2 bg-white border border-[#e5e5e5] rounded-md text-[#1a1a1a] text-sm resize-none"
                        rows={2}
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#4a4a4a] mb-1 block">City</label>
                      <Input
                        value={newPortfolio.city}
                        onChange={(e) => setNewPortfolio({...newPortfolio, city: e.target.value})}
                        placeholder="Enter city"
                        className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#4a4a4a] mb-1 block">Country</label>
                      <Input
                        value={newPortfolio.country}
                        onChange={(e) => setNewPortfolio({...newPortfolio, country: e.target.value})}
                        placeholder="Enter country"
                        className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#4a4a4a] mb-1 block">Pincode</label>
                      <Input
                        value={newPortfolio.pincode}
                        onChange={(e) => setNewPortfolio({...newPortfolio, pincode: e.target.value})}
                        placeholder="Enter pincode"
                        className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#4a4a4a] mb-1 block">PAN</label>
                      <Input
                        value={newPortfolio.pan}
                        onChange={(e) => setNewPortfolio({...newPortfolio, pan: e.target.value.toUpperCase()})}
                        placeholder="Enter PAN"
                        className="bg-white border-[#e5e5e5] text-[#1a1a1a]"
                        maxLength={10}
                      />
                    </div>
                  </>
                )}

                <div className="flex gap-3 justify-end pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddPortfolio(false)}
                    className="border-[#e5e5e5] text-[#4a4a4a] hover:bg-[#f5f5f5]"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#ffd87a] hover:bg-[#ffd87a] text-white">
                    Save
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
